import { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import ScaleLoader from "react-spinners/ScaleLoader";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import { formatNumber } from "../../utils/formatNumber";
import { ACCOUNT_TYPES } from "./ChartOfAccounts";
import "../../Styles/yellowcalfcss/Finance.css";

/**
 * Trial Balance — read-only proof that the ledger balances. Lists every GL
 * account with its closing debit/credit balance for the selected period and
 * verifies total debits === total credits.
 */

const authPayload = () => ({
  customer_id: Number(localStorage.getItem("customer_id")),
  api_key: localStorage.getItem("api_key"),
  user_id: Number(localStorage.getItem("user_id")),
  language: "en",
});

const today = () => new Date().toISOString().slice(0, 10);

const startOfYear = () => `${new Date().getFullYear()}-01-01`;

export function TrialBalance() {
  const customer_currency = (
    localStorage.getItem("customer_currency") || ""
  ).replace(/"/g, "");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [fromDate, setFromDate] = useState(startOfYear());
  const [toDate, setToDate] = useState(today());
  const [typeFilter, setTypeFilter] = useState("");

  const fetchTrialBalance = async () => {
    setLoading(true);

    const requestBody = {
      ...authPayload(),
      from_date: fromDate,
      to_date: toDate,
      account_type: typeFilter,
    };

    try {
      const res = await simplePostCall(
        ApiConfig.trialBalance,
        JSON.stringify(requestBody)
      );

      if (res?.result) {
        setRows(res.data || []);
      } else {
        setRows([]);
        toast.error(res?.message || "Failed to load trial balance");
      }
    } catch {
      setRows([]);
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrialBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = rows.reduce(
    (acc, r) => {
      acc.debit += Number(r.debit_balance) || 0;
      acc.credit += Number(r.credit_balance) || 0;
      return acc;
    },
    { debit: 0, credit: 0 }
  );

  const difference = totals.debit - totals.credit;
  const isBalanced = Math.abs(difference) < 0.005;

  const typeLabel = (type) =>
    ACCOUNT_TYPES.find((t) => t.value === type)?.label || "—";

  const money = (value) => `${customer_currency} ${formatNumber(value || 0)}`;

  // Group rows under their account type with per-section subtotals — an
  // accountant reads a trial balance by section, not as one flat list.
  const sections = ACCOUNT_TYPES.map((type) => {
    const sectionRows = rows.filter((r) => r.account_type === type.value);
    const subtotal = sectionRows.reduce(
      (acc, r) => {
        acc.debit += Number(r.debit_balance) || 0;
        acc.credit += Number(r.credit_balance) || 0;
        return acc;
      },
      { debit: 0, credit: 0 }
    );
    return { ...type, rows: sectionRows, subtotal };
  }).filter((s) => s.rows.length > 0);

  // Anything with an unrecognised account_type still has to be shown, or the
  // section totals would silently disagree with the grand total.
  const knownTypes = ACCOUNT_TYPES.map((t) => t.value);
  const otherRows = rows.filter((r) => !knownTypes.includes(r.account_type));
  if (otherRows.length > 0) {
    sections.push({
      value: "other",
      label: "Other",
      rows: otherRows,
      subtotal: otherRows.reduce(
        (acc, r) => {
          acc.debit += Number(r.debit_balance) || 0;
          acc.credit += Number(r.credit_balance) || 0;
          return acc;
        },
        { debit: 0, credit: 0 }
      ),
    });
  }

  const handlePrint = () => window.print();

  return (
    <div className="finance-page-container">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* HEADER */}
      <div className="special-header panel-white-all">
        <h4 className="page-title">Trial Balance</h4>
        <div className="action-buttons-group">
          <Button className="btn btn-primary yc-no-print" onClick={handlePrint}>
            Print
          </Button>
        </div>
      </div>

      <div className="yc-list-body">
        {/* FILTERS */}
        <div className="yc-list-filter yc-no-print">
          <Form.Control
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            title="From date"
          />
          <Form.Control
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            title="To date"
          />
          <Form.Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">All Account Types</option>
            {ACCOUNT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Form.Select>
          <Button
            className="btn btn-primary"
            onClick={fetchTrialBalance}
            disabled={loading}
          >
            {loading ? "Loading..." : "Run Report"}
          </Button>
        </div>

        {/* SUMMARY */}
        {!loading && rows.length > 0 && (
          <div className="fin-stat-row">
            <div className="fin-stat-card fin-accent-all">
              <span className="fin-stat-label">Accounts</span>
              <span className="fin-stat-value">{rows.length}</span>
              <span className="fin-stat-sub">With a balance</span>
            </div>
            <div className="fin-stat-card fin-accent-asset">
              <span className="fin-stat-label">Total Debits</span>
              <span className="fin-stat-value">{money(totals.debit)}</span>
              <span className="fin-stat-sub">All sections</span>
            </div>
            <div className="fin-stat-card fin-accent-income">
              <span className="fin-stat-label">Total Credits</span>
              <span className="fin-stat-value">{money(totals.credit)}</span>
              <span className="fin-stat-sub">All sections</span>
            </div>
            <div
              className={`fin-stat-card ${
                isBalanced ? "fin-accent-income" : "fin-accent-expense"
              }`}
            >
              <span className="fin-stat-label">Difference</span>
              <span className="fin-stat-value">
                {money(Math.abs(difference))}
              </span>
              <span className="fin-stat-sub">
                {isBalanced ? "Balanced" : "Needs investigation"}
              </span>
            </div>
          </div>
        )}

        {/* BALANCE STATUS */}
        {!loading && rows.length > 0 && (
          <div className="fin-balance-banner">
            <span
              className={
                isBalanced
                  ? "fin-balance-pill fin-balance-ok"
                  : "fin-balance-pill fin-balance-bad"
              }
            >
              {isBalanced
                ? "Ledger is balanced"
                : `Out of balance by ${money(Math.abs(difference))}`}
            </span>
            <span className="fin-balance-period">
              {fromDate} to {toDate}
            </span>
          </div>
        )}

        {/* TABLE HEADER */}
        <div className="yc-list-thead-card">
          <div className="yc-list-grid yc-list-grid-6">
            <div className="yc-list-cell">Sr.No</div>
            <div className="yc-list-cell">Account Code</div>
            <div className="yc-list-cell">Account Name</div>
            <div className="yc-list-cell">Type</div>
            <div className="yc-list-cell">Debit</div>
            <div className="yc-list-cell">Credit</div>
          </div>
        </div>

        {/* TABLE BODY */}
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "20vh" }}
          >
            <ScaleLoader color="#F4BB44" />
          </div>
        ) : rows.length === 0 ? (
          <div className="fin-empty">
            <span className="fin-empty-title">Nothing to report yet</span>
            <span className="fin-empty-text">
              No ledger balances exist for {fromDate} to {toDate}. The trial
              balance is built from posted journal entries — post some entries,
              or widen the date range.
            </span>
          </div>
        ) : (
          <>
            {sections.map((section) => (
              <div key={section.value}>
                <div className="fin-section-head">
                  <span className="fin-section-title">{section.label}</span>
                  <span className="fin-section-count">
                    {section.rows.length}{" "}
                    {section.rows.length === 1 ? "account" : "accounts"}
                  </span>
                </div>

                <div className="yc-list-row-list">
                  {section.rows.map((record, index) => (
                    <div
                      key={record.account_id || `${section.value}-${index}`}
                      className="yc-list-row-card"
                    >
                      <div className="yc-list-grid yc-list-grid-6">
                        <div className="yc-list-cell">{index + 1}</div>
                        <div className="yc-list-cell fw-semibold">
                          {record.account_code}
                        </div>
                        <div className="yc-list-cell">{record.account_name}</div>
                        <div className="yc-list-cell">
                          <span
                            className={`fin-type-pill fin-type-${record.account_type}`}
                          >
                            {typeLabel(record.account_type)}
                          </span>
                        </div>
                        <div className="yc-list-cell">
                          {Number(record.debit_balance) > 0
                            ? money(record.debit_balance)
                            : "—"}
                        </div>
                        <div className="yc-list-cell">
                          {Number(record.credit_balance) > 0
                            ? money(record.credit_balance)
                            : "—"}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* SECTION SUBTOTAL */}
                  <div className="yc-list-row-card fin-subtotal-row">
                    <div className="yc-list-grid yc-list-grid-6">
                      <div className="yc-list-cell" />
                      <div className="yc-list-cell" />
                      <div className="yc-list-cell fw-semibold">
                        {section.label} Subtotal
                      </div>
                      <div className="yc-list-cell" />
                      <div className="yc-list-cell fw-semibold">
                        {money(section.subtotal.debit)}
                      </div>
                      <div className="yc-list-cell fw-semibold">
                        {money(section.subtotal.credit)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* GRAND TOTALS */}
            <div className="yc-list-row-card fin-totals-row">
              <div className="yc-list-grid yc-list-grid-6">
                <div className="yc-list-cell" />
                <div className="yc-list-cell" />
                <div className="yc-list-cell fw-bold">Grand Total</div>
                <div className="yc-list-cell" />
                <div className="yc-list-cell fw-bold">{money(totals.debit)}</div>
                <div className="yc-list-cell fw-bold">
                  {money(totals.credit)}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default TrialBalance;
